import classNames from "classnames";
import React, { FC, useEffect } from "react";
import { Helmet } from "react-helmet";

import { MARKER_SVG_MARKUP } from "./markerSVGMarkup";

import "./_here-map.scss";

const HERE_API_KEY = window.__HERE_KEY__ !== "<HERE_KEY>" && window.__HERE_KEY__;

export interface IHereMarker {
  lat: string;
  lng: string;
}

const addMarkers = (map: any, markers: IHereMarker[]) => {
  const hereMarkers: any = [];
  const { H } = window;
  const icon = new H.map.Icon(MARKER_SVG_MARKUP);

  markers.forEach(marker => {
    const hereMarker = new H.map.Marker(marker, { icon });
    hereMarkers.push(hereMarker);
    map.addObject(hereMarker);
  });

  const group = new H.map.Group();
  group.addObjects(hereMarkers);
  map.addObject(group);

  map.getViewModel().setLookAtData({
    bounds: group.getBoundingBox(),
  });
};

interface IHereMapProps {
  className?: string;
  markers?: IHereMarker[];
  zoom?: number;
  hasScaleBar?: boolean;
  hasZoom?: boolean;
  hasMapSettings?: boolean;
}

export const HereMap: FC<IHereMapProps> = ({
  className,
  markers = [],
  zoom,
  hasScaleBar = true,
  hasZoom = true,
  hasMapSettings = true,
}) => {
  useEffect(() => {
    if (!HERE_API_KEY) {
      return;
    }
    const { H } = window;
    const platform = new H.service.Platform({
      apikey: HERE_API_KEY,
    });
    const defaultLayers = platform.createDefaultLayers();

    const map = new H.Map(document.getElementById("hereMap"), defaultLayers.vector.normal.map, {
      pixelRatio: window.devicePixelRatio || 1,
      padding: { top: 30, left: 30, bottom: 30, right: 30 },
    });

    window.addEventListener("resize", () => map.getViewPort().resize());

    // The constructor call below has side effects and adds mouse drag event to the map.
    new H.mapevents.Behavior(new H.mapevents.MapEvents(map));
    const ui = H.ui.UI.createDefault(map, defaultLayers);

    ui.getControl("scalebar").setVisibility(hasScaleBar);
    ui.getControl("mapsettings").setVisibility(hasMapSettings);
    ui.getControl("zoom").setVisibility(hasZoom);
    addMarkers(map, markers);

    if (zoom) {
      map.setZoom(zoom);
    }
  }, []);

  const rootClass = classNames("here-map", className);

  return (
    <>
      <Helmet>
        <link rel="stylesheet" type="text/css" href="https://js.api.here.com/v3/3.1/mapsjs-ui.css" />
      </Helmet>
      <div className={rootClass} id="hereMap" />
    </>
  );
};
